import React, {
  createRef,
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  TripInfoTaskType,
  InAppModalGroupRef,
  TripCheckInOutType,
} from '../types';
import { ClockInModal } from './clock-in/clock-in-modal';
import { TripInfoModal } from './trip-info/trip-info-modal';
import {
  APP_TYPE,
  dispatch,
  ErrorVariants,
  getCheckInInfo,
  getCurrentCustomer,
  getCustomerInfo,
  logOut,
  OfflineCurrentCustomerMetadata,
  OfflineTaskType,
  resetOrderCart,
  showToast,
  updateAppType,
  updateCheckInInfo,
  updateCustomerInfo,
  useAppSelector,
  useLogoutMutation,
} from '@zonofi/zono-money-store';
import { useLocation, useNavigate } from 'react-router-dom';
import { FIELD_OPS_ROUTES } from '../constants';
import { addTaskToQueue, loadQueue, offlineQueue } from '../queue/offline';
import { TripCheckOutModal } from './check-out/trip-check-out-modal';

export const inAppModalGroupRef = createRef<InAppModalGroupRef>();

const isTripInfoTaskType = (
  type: TripInfoTaskType | TripCheckInOutType
): type is TripInfoTaskType => {
  return (Object.values(TripInfoTaskType) as string[]).includes(type);
};

export const InAppModalGroup = forwardRef(
  (props, ref: Ref<InAppModalGroupRef>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const checkInInfo = useAppSelector(getCheckInInfo);
    const previousCustomer = useAppSelector(getCustomerInfo);
    const currentCustomer = useAppSelector(getCurrentCustomer);

    const [clockInModalOpen, setClockInModalOpen] = useState(false);
    const [tripInfoModalOpen, setTripInfoModalOpen] = useState(false);
    const [tripInfoType, setTripInfoType] = useState<TripInfoTaskType>();
    const [tripCheckInOutType, setTripCheckInOutType] =
      useState<TripCheckInOutType>();
    const [showCheckOutModal, setShowCheckOutModal] = useState(false);

    const [logoutApi] = useLogoutMutation();

    const customerId = useMemo(() => {
      if (
        checkInInfo &&
        tripInfoType &&
        [
          TripInfoTaskType.ALREADY_CHECKED_IN,
          TripInfoTaskType.NOT_CHECKED_OUT,
        ].includes(tripInfoType)
      ) {
        return checkInInfo.customerId;
      }
      if (tripInfoType === TripInfoTaskType.NOT_CHECKED_IN) {
        const cIdFrompath = location.pathname.split('/').slice(-1);
        return cIdFrompath[0];
      }
      return '';
    }, [checkInInfo, tripInfoType]);

    useImperativeHandle(ref, () => ({
      openClockInModal: () => setClockInModalOpen(true),
      openTripInfoModal: (type) => {
        setTripInfoType(type);
        setTripInfoModalOpen(true);
      },
      openCheckOutModal: (type) => {
        setTripCheckInOutType(type);
        setShowCheckOutModal(true);
      },
    }));

    const clearCaches = () => {
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_RUNTIME_CACHES',
        });
      }
    };

    const onCheckOutCall = async (
      type: TripInfoTaskType | TripCheckInOutType
    ) => {
      const isTripInfo = isTripInfoTaskType(type);
      const resolvedCustomerId = isTripInfo
        ? customerId
        : currentCustomer?.customerId;

      const resolvedCustomer = isTripInfo
        ? (previousCustomer as OfflineCurrentCustomerMetadata | undefined)
        : ((previousCustomer as OfflineCurrentCustomerMetadata | undefined) ??
          currentCustomer);

      const onSuccessHandler = isTripInfo
        ? () => onCheckSuccess(type)
        : onCheckOutSuccess;

      await addTaskToQueue({
        type: OfflineTaskType.CHECK_OUT,
        entityKey: `${OfflineTaskType.CHECK_OUT.toLowerCase()}:${resolvedCustomerId}-${Date.now().toString()}`,
        payload: { checkInId: checkInInfo?.checkInId },
        customer: resolvedCustomer,
        onSuccess: onSuccessHandler,
      });
    };

    const onCheckSuccess = (type: TripInfoTaskType) => {
      dispatch(updateCheckInInfo(undefined));
      dispatch(updateCustomerInfo(undefined));
      setTimeout(async () => {
        dispatch(
          showToast({
            message: 'Check-Out Successful',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );

        if (type === TripInfoTaskType.NOT_CHECKED_OUT) {
          const { queue = [] } = await loadQueue();
          if (queue.length > 0) {
            inAppModalGroupRef?.current?.openTripInfoModal(
              TripInfoTaskType.NOT_SYNC
            );
            return;
          } else {
            setClockInModalOpen(true);
          }
        } else if (type === TripInfoTaskType.ALREADY_CHECKED_IN) {
          navigate(
            FIELD_OPS_ROUTES.CUSTOMER_CHECK_IN?.replace(
              ':customerId',
              currentCustomer?.customerId ?? ''
            )
          );
          return;
        }
      }, 100);
    };

    const onCheckOutSuccess = () => {
      dispatch(updateCheckInInfo(undefined));
      inAppModalGroupRef?.current?.openCheckOutModal(
        TripCheckInOutType.CHECK_OUT_SUCCESS
      );
    };

    const handleNotSyncedTrip = async (type?: 'log-out' | 'clock-out') => {
      await offlineQueue.clearQueue();
      dispatch(updateCheckInInfo(undefined));
      dispatch(updateCustomerInfo(undefined));
      dispatch(resetOrderCart());

      if (type === 'log-out') {
        try {
          await logoutApi().unwrap();
        } catch (error) {
          console.log('Logout API failed, proceeding anyway', error);
        } finally {
          clearCaches();
          setTimeout(() => {
            dispatch(logOut());
            dispatch(updateAppType(APP_TYPE.FIELDS_OPS));
          }, 100);
        }
        return;
      } else if (type === 'clock-out') {
        setClockInModalOpen(true);
        return;
      }
    };

    const onPrimaryTripButtonPress = useCallback(() => {
      const isCustomerPage =
        location.pathname === FIELD_OPS_ROUTES.CUSTOMER_LIST;
      setTripInfoModalOpen(false);

      switch (tripInfoType) {
        case TripInfoTaskType.NOT_CLOCKED_IN:
          setClockInModalOpen(true);
          break;

        case TripInfoTaskType.ALREADY_CHECKED_IN:
        case TripInfoTaskType.NOT_CHECKED_OUT:
          onCheckOutCall(tripInfoType as TripInfoTaskType);
          break;

        case TripInfoTaskType.NOT_CHECKED_IN:
          navigate(
            FIELD_OPS_ROUTES.CUSTOMER_CHECK_IN?.replace(
              ':customerId',
              customerId
            ),
            { replace: !isCustomerPage }
          );
          break;

        case TripInfoTaskType.NOT_SYNCED_CLOCK_OUT:
        case TripInfoTaskType.NOT_SYNCED_LOG_OUT:
        case TripInfoTaskType.NOT_SYNC:
          navigate(FIELD_OPS_ROUTES.SYNC);
          break;
      }
    }, [customerId, tripInfoType, location.pathname]);

    const onSecondaryTripButtonPress = async () => {
      setTripInfoModalOpen(false);
      if (tripInfoType === TripInfoTaskType.NOT_SYNC) {
        await handleNotSyncedTrip();
      } else if (tripInfoType === TripInfoTaskType.NOT_SYNCED_LOG_OUT) {
        await handleNotSyncedTrip('log-out');
      } else if (tripInfoType === TripInfoTaskType.NOT_SYNCED_CLOCK_OUT) {
        await handleNotSyncedTrip('clock-out');
      }
    };

    return (
      <React.Fragment>
        <ClockInModal
          open={clockInModalOpen}
          onClose={() => setClockInModalOpen(false)}
        />
        <TripInfoModal
          open={tripInfoModalOpen}
          onClose={() => setTripInfoModalOpen(false)}
          onPrimaryPress={onPrimaryTripButtonPress}
          type={tripInfoType as TripInfoTaskType}
          onSecondaryPress={onSecondaryTripButtonPress}
        />
        <TripCheckOutModal
          open={showCheckOutModal}
          onClose={() => setShowCheckOutModal(false)}
          type={tripCheckInOutType as TripCheckInOutType}
          onCheckOutPress={() =>
            onCheckOutCall(tripCheckInOutType as TripCheckInOutType)
          }
        />
      </React.Fragment>
    );
  }
);
